/**
 * MobileReflectionFlowPreview — preview-route for testing the new mobile
 * reflection UX from desktop. Loads the same V1 template the player would see
 * and renders the new flow full-screen. No DB writes — submit is a no-op.
 *
 * Route: /mobile-reflection-preview/:evaluationId  (param can be an existing
 *        evaluation_request id; if not found it falls back to a synthetic
 *        evaluation built from /api/templates/v2/{templateId}).
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import MobileReflectionFlow from "./MobileReflectionFlow";

export default function MobileReflectionFlowPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template");
  const [evaluation, setEvaluation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (templateId) {
          // Build a synthetic eval from a V2 template (handy for fresh demos).
          const { data: tpl } = await api.get(`/templates/v2/${templateId}`);
          if (!active) return;
          setEvaluation({
            id: `preview-${templateId}`,
            template_id: templateId,
            template_name: tpl.name,
            template: tpl,
            questions: tpl.questions,
            responses: [],
          });
          return;
        }
        const { data } = await api.get(`/evaluations/${id}`);
        if (active) setEvaluation(data);
      } catch (e) {
        console.error(e);
        if (active) setError(e?.response?.data?.detail || "Kunde inte ladda");
      }
    })();
    return () => {
      active = false;
    };
  }, [id, templateId]);

  if (error) {
    return (
      <div
        data-testid="mobile-reflection-preview-error"
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center text-muted-foreground"
      >
        <p className="font-medium text-[rgb(var(--tn-text-ffffff))]">Något gick fel</p>
        <p className="mt-2 text-sm">{error}</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-6 rounded-full px-5 py-2 bg-primary text-primary-foreground text-sm"
        >
          Tillbaka
        </button>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Laddar…
      </div>
    );
  }

  return (
    <MobileReflectionFlow
      evaluation={evaluation}
      onClose={() => navigate(-1)}
      onSubmit={async (_, responses) => {
        console.log("[MobileReflectionFlowPreview] responses", responses);
        toast.success("Förhandsgranskning — inget sparades");
      }}
    />
  );
}
